import type { TemplateListItemRes } from 'types';
import type { GetSingleHeliosRecord, HeliosRecordDataSection, HeliosRecordDataTablesRes } from 'types/dataHelios';
import type { RecordSection, TabInfo } from 'types/helios';
import type { TranslationProps } from 'react-i18next';
import { getStartPos } from 'utils/section/gridUtils';
import { getCurrentLanguageContent } from './language';

type RawValue = RecordSection['formItems'][number]['formItem']['definition']['dataRecordColumn']['rawValue'];

export const heliosGridSort = (section: RecordSection) =>
  section.formItems.toSorted((a, b) => {
    const startY_A = getStartPos(a.formItem.y);
    const startY_B = getStartPos(b.formItem.y);

    if (startY_A === startY_B) {
      const startX_A = getStartPos(a.formItem.x);
      const startX_B = getStartPos(b.formItem.x);

      return startX_A - startX_B;
    }

    return startY_A - startY_B;
  });

export const mappingPropsToHeliosGrid = (data?: GetSingleHeliosRecord, i18n?: TranslationProps['i18n']): RecordSection[] =>
  data?.sections?.map((section) => ({
    id: section?.title,
    name: getCurrentLanguageContent(i18n, section?.translations) ?? section?.name,
    description: section?.description,
    collapsed: section?.collapsed,
    gridSizeX: section?.sizeX,
    gridSizeY: section?.sizeY,
    tabId: section?.tabId ?? null,
    subTabs:
      section?.subTabs?.map((st) => ({
        id: st.id,
        order: st.order,
        name: getCurrentLanguageContent(i18n, st?.translations ?? undefined) ?? st?.name ?? null,
        translations: st?.translations ?? null,
      })) ?? null,
    formItems: section?.items.map((item) => ({
      formItem: {
        id: String(item?.dataRecordColumn?.itemId ?? `${item?.x}-${item?.y}`),
        name: item?.dataRecordColumn?.columnName,
        title: getCurrentLanguageContent(i18n, item?.translations) ?? item?.label,
        x: item?.x,
        y: item?.y,
        subTabId: item?.subTabId ?? null,
        definition: {
          ...item,
        },
      },
    })),
  })) ?? [];

export const mappingTabs = (data?: GetSingleHeliosRecord): TabInfo[] =>
  data?.tabs?.map((tab) => ({
    id: tab.id,
    order: tab.order,
    title: tab.title,
    titleTranslations: tab.titleTranslations,
  })) ?? [];

export const isRawValueObject = (value: RawValue) => {
  return typeof value === 'object' && value !== null && 'id' in value && 'name' in value;
};

export const mappingDefaultValuesToForm = (arr: RecordSection[]) =>
  arr?.reduce((acc, section) => {
    const items = section?.formItems.reduce((prev: { [key: string]: string | RawValue }, { formItem }) => {
      const value = formItem?.definition?.dataRecordColumn?.rawValue;

      prev[formItem?.id] = value;
      return prev;
    }, {});

    return { ...acc, ...items };
  }, {});

export const mappingEntriesToSections = (
  entries: HeliosRecordDataTablesRes[],
  lists: TemplateListItemRes[],
  placeholder: string,
  i18n?: TranslationProps['i18n']
): HeliosRecordDataSection[] =>
  entries.map((entry, index) => ({
    id: index,
    templateId: lists?.[index]?.id,
    name:
      getCurrentLanguageContent(i18n, lists?.[index]?.translatesDisplayAs) ??
      (lists?.[index]?.displayAs.length ? lists?.[index].displayAs : placeholder),
    description:
      getCurrentLanguageContent(i18n, lists?.[index]?.translatesDescription) ??
      (lists?.[index]?.description.length ? lists?.[index].description : ''),
    collapsed: false,
    gridSizeX: 0,
    gridSizeY: 0,
    entry,
    datasourceId: lists?.[index]?.datasourceId,
  }));

export const getPrintRequest = (reportId: number, classId: number, recordNumber: number) => ({
  reportId,
  records: [{ recordNumber, classId, slaves: null }],
});
